// ─────────────────────────────────────────────────────────────────────────────────────────────
// COPIE VERSIONNÉE — AGRÉGATION INCHANGÉE.
// Origine   : dépôt VLT Devis, public/outils/metiers/cvc/zones.mjs
//             (C:\Users\wecha\vlt-devis-saas\public\outils\metiers\cvc\zones.mjs)
// Version   : commit 53f27dce35157919e1bdb92bf9dcd6c1e135e83c du 18/09/2026, fichier du 18/09/2026 17:59
// Empreinte : SHA-256 du source 316D41C34A3FCC39B7D54CDD65E236FCCB952F5B9AB3737B1BD19CC063D51275
// Copie     : 07/10/2026, portage TypeScript TEL QUEL (annotations de type seulement). Seul ajout, sans effet sur les
//             valeurs : la charge d'air neuf « à reprendre par la CTA » des pièces (adaptation A2 de froid.ts) est
//             additionnée par zone pour être affichée ; elle n'entre dans AUCUN besoin de terminal ni de groupe.
// Tests     : tests/unit/cvc-moteur.test.ts (tests 10, 16, 17 et 32 de l'origine, mêmes valeurs).
// ─────────────────────────────────────────────────────────────────────────────────────────────
// Assistant Climatisation CVC — agrégation ZONES et GROUPES, simultanéité. Moteur PUR.
//
// CDC §3.3 et §3.4 (docs/mes-outils/cahiers/CDC-CVC.md) :
//   • Pièce  : unité de saisie.
//   • Zone   : pièces desservies par UN MÊME terminal (gainable, grande cassette).
//   • Groupe : zones raccordées à UN MÊME groupe extérieur (multi-split).
//
// Deux simultanéités, à ne pas confondre :
//   1. CALCULÉE, toujours : la non-simultanéité SOLAIRE. Le solaire d'une zone est le maximum sur
//      les trois créneaux de la SOMME des vitrages de ses pièces — et non la somme des maximums
//      pièce par pièce. Deux pièces est et ouest ne prennent pas leur pointe à la même heure.
//      Aucune justification n'est demandée : c'est un fait de calcul.
//   2. DÉCLARÉE, optionnelle : le coefficient s de 0,5 à 1. Il s'applique UNIQUEMENT au besoin
//      du GROUPE, jamais aux terminaux (qui restent dimensionnés pièce par pièce ou zone par
//      zone), jamais au chaud. Justification obligatoire si s < 1 (règle 1.0.0 conservée, F08).
import type { ResultatFroid } from './froid.ts';
import type { ResultatChaud } from './chaud.ts';

const n = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : null);

export interface ZoneSaisie { id: string; nom: string; terminaux?: number | null }
export interface GroupeSaisi { id: string; nom: string; zones?: string[] }

export interface ResultatZone {
  id: string; nom: string; pieces: string[]; incomplet: boolean; surface_m2: number;
  sensible_w: number; sensible_hors_solaire_w: number; solaire_w: number; solaire_creneaux_w: number[];
  creneau: string; creneau_nom: string; latent_w: number; total_w: number;
  total_pointes_additionnees_w: number; economie_non_simultaneite_w: number; shr: number | null;
  dimensionnement_froid_w: number; dimensionnement_sensible_w: number;
  chaud_disponible: boolean; phi_w: number | null; dimensionnement_chaud_w: number | null; terminaux: number;
  /** Charge de l'air neuf à reprendre par la CTA (adaptation A2, affichage seulement) ; 0 sans air prétraité. */
  air_neuf_cta_w: number;
  /** P0-1 (22/10/2026) — charge de chauffage de l'air neuf jusqu'à l'état de soufflage, à reprendre par la CTA ; 0 sans air prétraité. */
  air_neuf_cta_chaud_w: number;
}

export interface ResultatGroupe {
  id: string; nom: string; zones: string[]; dimensionnement_terminaux_w: number; simultaneite: number;
  justification: string; dimensionnement_froid_w: number; dimensionnement_chaud_w: number | null; terminaux: number;
}

/**
 * Agrège les résultats de plusieurs pièces en une zone.
 * @param froids  résultats froid.calculerPiece() des pièces de la zone
 * @param chauds  résultats chaud.calculerPiece() des mêmes pièces (peut être vide)
 */
export function calculerZone(zone: ZoneSaisie, froids: ResultatFroid[], chauds: ResultatChaud[],
  { securite_froid_pct = 10, securite_chaud_pct = 10 }: { securite_froid_pct?: number; securite_chaud_pct?: number } = {}): ResultatZone {
  const utiles = froids.filter((f) => f && !f.manques.length && !f.erreur);
  const incomplet = froids.length !== utiles.length;

  // Sensible hors solaire + solaire pris au créneau le plus chargé POUR LA ZONE ENTIÈRE.
  const sensibleHorsSolaire = utiles.reduce((s, f) => s + f.sensible_hors_solaire_w * f.count, 0);
  const creneaux = [0, 1, 2].map((c) => utiles.reduce((s, f) => s + f.solaire_creneaux_w[c] * f.count, 0));
  let iCreneau = 0;
  for (let c = 1; c < 3; c++) if (creneaux[c] > creneaux[iCreneau]) iCreneau = c;
  const solaire = creneaux[iCreneau];
  const NOMS = ['Matin', 'Midi', 'Après-midi'];

  const sensible = sensibleHorsSolaire + solaire;
  const latent = utiles.reduce((s, f) => s + f.latent_w * f.count, 0);
  const total = sensible + latent;
  const surface = utiles.reduce((s, f) => s + f.surface_m2 * f.count, 0);

  // Somme naïve des pointes pièce par pièce : sert à MONTRER ce que la non-simultanéité économise.
  const totalSansSimultaneite = utiles.reduce((s, f) => s + f.total_w * f.count, 0);

  // Le chaud n'est agrégé que si TOUTES les pièces de la zone l'ont : un total partiel serait faux.
  const chaudsUtiles = (chauds || []).filter((c) => c && !c.refus && (!c.manques || !c.manques.length));
  const chaudComplet = chaudsUtiles.length > 0 && chaudsUtiles.length === utiles.length;
  const phi = chaudsUtiles.reduce((s, c) => s + (c.phi_w as number) * c.count, 0);

  return {
    id: zone.id,
    nom: zone.nom,
    pieces: utiles.map((f) => f.id),
    incomplet,
    surface_m2: surface,
    sensible_w: sensible,
    sensible_hors_solaire_w: sensibleHorsSolaire,
    solaire_w: solaire,
    solaire_creneaux_w: creneaux,
    creneau: ['matin', 'midi', 'apres_midi'][iCreneau],
    creneau_nom: NOMS[iCreneau],
    latent_w: latent,
    total_w: total,
    total_pointes_additionnees_w: totalSansSimultaneite,
    economie_non_simultaneite_w: totalSansSimultaneite - total,
    shr: total ? sensible / total : null,
    // Le terminal est dimensionné sur la zone, SANS coefficient de simultanéité déclaré.
    dimensionnement_froid_w: total * (1 + securite_froid_pct / 100),
    dimensionnement_sensible_w: sensible * (1 + securite_froid_pct / 100),
    chaud_disponible: chaudComplet,
    phi_w: chaudComplet ? phi : null,
    dimensionnement_chaud_w: chaudComplet ? phi * (1 + securite_chaud_pct / 100) : null,
    terminaux: n(zone.terminaux) && n(zone.terminaux)! > 0 ? n(zone.terminaux)! : 1,
    air_neuf_cta_w: utiles.reduce((s, f) => s + (f.air_neuf_cta ? f.air_neuf_cta.total_w * f.count : 0), 0),
    air_neuf_cta_chaud_w: chaudsUtiles.reduce((s, c) => s + (c.air_neuf_cta ? c.air_neuf_cta.puissance_w * c.count : 0), 0),
  };
}

/**
 * Besoin d'un GROUPE (un groupe extérieur) : somme simultanée de ses zones.
 * C'est le SEUL endroit où le coefficient de simultanéité déclaré s'applique.
 */
export function calculerGroupe(groupe: GroupeSaisi, zones: ResultatZone[], { s = 1, justification = '' }: { s?: number; justification?: string } = {}): ResultatGroupe {
  const miennes = zones.filter((z) => (groupe.zones || []).includes(z.id));
  const somme = miennes.reduce((a, z) => a + z.dimensionnement_froid_w, 0);
  const chaudComplet = miennes.length > 0 && miennes.every((z) => z.dimensionnement_chaud_w != null);
  const coef = n(s) == null ? 1 : s;
  return {
    id: groupe.id,
    nom: groupe.nom,
    zones: miennes.map((z) => z.id),
    // Somme des besoins de terminaux, avant simultanéité déclarée.
    dimensionnement_terminaux_w: somme,
    simultaneite: coef,
    justification: coef < 1 ? String(justification || '') : '',
    dimensionnement_froid_w: somme * coef,
    // Pas de simultanéité en chaud : tout le monde chauffe en même temps.
    dimensionnement_chaud_w: chaudComplet ? miennes.reduce((a, z) => a + (z.dimensionnement_chaud_w as number), 0) : null,
    terminaux: miennes.reduce((a, z) => a + z.terminaux, 0),
  };
}

/** Totaux du projet : ce qui s'affiche en tête et part dans result_data. */
export function totaux(zones: ResultatZone[], groupes: ResultatGroupe[]) {
  const froid = zones.reduce((s, z) => s + z.total_w, 0);
  const sensible = zones.reduce((s, z) => s + z.sensible_w, 0);
  const latent = zones.reduce((s, z) => s + z.latent_w, 0);
  const dimZones = zones.reduce((s, z) => s + z.dimensionnement_froid_w, 0);
  const chaudComplet = zones.length > 0 && zones.every((z) => z.dimensionnement_chaud_w != null);
  return {
    surface_m2: zones.reduce((s, z) => s + z.surface_m2, 0),
    sensible_w: sensible,
    latent_w: latent,
    total_froid_w: froid,
    shr: froid ? sensible / froid : null,
    dimensionnement_froid_terminaux_w: dimZones,
    dimensionnement_froid_groupes_w: groupes.length ? groupes.reduce((s, g) => s + g.dimensionnement_froid_w, 0) : dimZones,
    phi_w: chaudComplet ? zones.reduce((s, z) => s + (z.phi_w as number), 0) : null,
    dimensionnement_chaud_w: chaudComplet ? zones.reduce((s, z) => s + (z.dimensionnement_chaud_w as number), 0) : null,
    terminaux: zones.reduce((s, z) => s + z.terminaux, 0),
    air_neuf_cta_w: zones.reduce((s, z) => s + z.air_neuf_cta_w, 0),
    air_neuf_cta_chaud_w: zones.reduce((s, z) => s + (z.air_neuf_cta_chaud_w || 0), 0),
  };
}
